import { Component, OnDestroy, OnInit } from '@angular/core';

import { FormControl, FormGroup } from '@angular/forms';

import { QueryRef } from 'apollo-angular';
import { SelectItem } from 'primeng/api';
import { SubSink } from 'subsink';

import {
  HistoryPartsFragment,
  ListHistoryDataOnlyGQL,
  ListHistoryDataOnlyQuery,
  ListHistoryDataOnlyQueryVariables,
  ListHistoryFilterInput,
  ListHistoryQueryVariables,
} from '../../generated/graphql.generated';
import { pagination } from '../global.constants';
import { BrandingService } from '../services/branding.service';
import { DetailsHelperService } from '../services/details-helper.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { MarkdownHelperService } from '../services/markdown.service';
import { WatchQueryHelper } from '../utilities/watchQueryHelper';

@Component({
  selector: 'app-history-search',
  templateUrl: './history-search.component.html',
  styleUrl: './history-search.component.scss'
})
export class HistorySearchComponent implements OnInit, OnDestroy {

  watchQuery: QueryRef<ListHistoryDataOnlyQuery, ListHistoryDataOnlyQueryVariables>;

  historyQH: WatchQueryHelper = {
    limit: 10,
    skip: 0,
    loading: true,
    hasMore: true,
    mergeNextResults: false,
  };

  placeholderCount = Array.from({ length: this.historyQH.limit });

  history: HistoryPartsFragment[] = [];

  name?: string;

  objectLabels: SelectItem<string>[] = [
    {
      value: 'User',
      label: 'User',
    },
    {
      value: 'CalendarEvent',
      label: 'Calendar Event',
    },
    {
      value: 'Job',
      label: 'Job',
    },
    {
      value: 'Asset',
      label: 'Asset',
    },
    {
      value: 'Zone',
      label: 'Zone',
    },
    {
      value: 'Price',
      label: 'Price',
    },
  ]

  historyFilterForm = new FormGroup({
    actions: new FormControl([]),
    objectLabel: new FormControl([]),
    objectIds: new FormControl([]),
    user: new FormControl(undefined),
  });

  pagination = pagination;

  private subs = new SubSink();

  constructor(
    private historyGQL: ListHistoryDataOnlyGQL,
    public markdownHelper: MarkdownHelperService,
    public brandingService: BrandingService,
    private localNotify: FreyaNotificationsService,
    private detailsHelper: DetailsHelperService,
  ) {}

  ngOnInit(): void {
    this.subs.sink = this.historyFilterForm.valueChanges.subscribe(() => {
      this.historyQH.skip = 0;
      this.historyQH.hasMore = true;
      this.historyQH.mergeNextResults = false;
      this.fetchHistory();
    });

    this.fetchHistory();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  fetchHistory() {
    if (this.watchQuery) {
      this.watchQuery.refetch(this.getQueryVars());
      return;
    }

    this.watchQuery = this.historyGQL.watch(this.getQueryVars(), {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

    this.subs.sink = this.watchQuery.valueChanges.subscribe((res) => {
      this.historyQH.loading = res.loading;
      if (res.loading) { return; }

      if (this.historyQH.mergeNextResults) {
        this.history = [
          ...this.history,
          ...res.data.history.history,
        ];
      } else {
        this.history = res.data.history.history;
      }

      this.historyQH.hasMore = res.data.history.history.length === this.historyQH.limit;
    }, (err) => {
      this.localNotify.apolloError('Failed to fetch history', err);
    });
  }

  getQueryVars(): ListHistoryQueryVariables {

    const { value } = this.historyFilterForm;
    const filter: ListHistoryFilterInput = {
      zoneIds: this.brandingService.currentZone().value?.id ? [this.brandingService.currentZone().value?.id] : undefined,
      objectIds: value.objectIds?.length > 0 ? value.objectIds : undefined,
      objectLabel: value.objectLabel?.length > 0 ? value.objectLabel : undefined,
      userIds: value.user?.id ? [value.user.id] : undefined,
      types: value.actions?.length > 0 ? value.actions : undefined,
    };
    const mutableFilter = filter as Record<string, unknown>;

    // The API doesn't handle empty values very well
    for (const key of Object.keys(mutableFilter)) {
      const filterValue = mutableFilter[key];

      if (Array.isArray(filterValue) && !filterValue.length) {
        delete mutableFilter[key];
      } else if (!filterValue) {
        delete mutableFilter[key];
      }
    }

    return {
      input: {
        filter,
        limit: this.historyQH.limit,
        skip: this.historyQH.skip,
        sort: 'createdAt:DESC',
      },
    };
  }

  fetchMore() {
    if (this.historyQH.loading || !this.historyQH.hasMore) { return; }
    this.historyQH.skip += this.historyQH.limit;
    this.historyQH.mergeNextResults = true;
    this.fetchHistory();
  }

  selectHitory(historyId: string) {
    this.detailsHelper.detailsItem.next({ type: 'history', item: historyId });
  }
}
